import React, { useState, useEffect, useMemo } from 'react';
import {
    ACCOUNT_TYPES,
    ACCOUNT_TYPE_LABELS,
    ACCOUNT_TYPE_BADGES,
    inferAccountType
} from '../../constants';
import { formatCurrency, formatDate } from '../../utils';
import { MonthSelector, getDateRangeFromMonths, AccountSelect } from '../common';
import { getJournalHistory } from '../../hooks/useFileSystem';

const GeneralLedger = ({
    accounts,
    journals,
    currentFiscalYear,
    fiscalYears,
    setFocusJournalId,
    setCurrentPage,
    deletedJournals,
    accountType,
    user,
    activeBookEmail
}) => {
    const dataEmail = activeBookEmail || user?.email;
    const isKojin = accountType === 'kojin';
    const [selectedAccount, setSelectedAccount] = useState('all');
    const [selectedMonths, setSelectedMonths] = useState([0]);
    const [modifiedEntries, setModifiedEntries] = useState([]);
    const [loadingModified, setLoadingModified] = useState(false);

    // 現在の年度のendMonthを取得
    const currentEndMonth = useMemo(() => {
        const currentFY = fiscalYears?.find(fy => fy.year === currentFiscalYear);
        return currentFY?.endMonth || (isKojin ? 12 : 3);
    }, [fiscalYears, currentFiscalYear, isKojin]);

    const jumpToJournal = (journalId) => {
        setFocusJournalId(journalId);
        setCurrentPage('journals');
    };

    const loadModifiedHistory = async () => {
        if (!dataEmail) {
            console.warn('loadModifiedHistory: dataEmail is missing');
            return;
        }
        // kojinの場合は必ず12月決算
        const actualEndMonth = isKojin ? 12 : currentEndMonth;
        console.log('loadModifiedHistory: Loading history for', dataEmail, currentFiscalYear, actualEndMonth, '(original endMonth:', currentEndMonth, ')');
        setLoadingModified(true);
        try {
            const history = await getJournalHistory(dataEmail, currentFiscalYear, actualEndMonth);
            console.log('loadModifiedHistory: Received history:', history);
            const allHistory = history
                .sort((a, b) => new Date(b.operationTimestamp) - new Date(a.operationTimestamp));
            setModifiedEntries(allHistory);
            console.log('loadModifiedHistory: Set modified entries:', allHistory.length);
        } catch (err) {
            console.error('Failed to load modified history:', err);
        }
        setLoadingModified(false);
    };

    useEffect(() => {
        if (selectedAccount === '_MODIFIED_') {
            loadModifiedHistory();
        }
    }, [selectedAccount, currentFiscalYear, dataEmail]);

    const { dateFrom, dateTo } = getDateRangeFromMonths(selectedMonths, currentFiscalYear, isKojin);

    const nullEntries = useMemo(() => {
        return journals
            .filter(j => {
                if (dateFrom && j.date < dateFrom) return false;
                if (dateTo && j.date > dateTo) return false;
                if (!j.date || !j.description) return true;
                return j.lines.some(l => !l.accountId || !accounts.find(a => a.id === l.accountId) || !l.amount);
            })
            .sort((a, b) => new Date(b.date) - new Date(a.date))
            .map(j => ({
                journalId: j.id,
                date: j.date,
                description: j.description,
                lines: j.lines
            }));
    }, [journals, accounts, dateFrom, dateTo]);

    const deletedEntries = useMemo(() => {
        return (deletedJournals || [])
            .filter(j => {
                if (dateFrom && j.date < dateFrom) return false;
                if (dateTo && j.date > dateTo) return false;
                return true;
            })
            .sort((a, b) => new Date(b.deletedAt || b.date) - new Date(a.deletedAt || a.date))
            .map(j => ({
                journalId: j.id,
                date: j.date,
                description: j.description,
                deletedAt: j.deletedAt,
                deletedBy: j.deletedBy,
                deletedByName: j.deletedByName,
                lines: j.lines
            }));
    }, [deletedJournals, dateFrom, dateTo]);

    const sortedAccounts = useMemo(() => {
        return [...accounts].sort((a, b) => {
            const typeOrder = { asset: 1, liability: 2, equity: 3, revenue: 4, expense: 5 };
            if (typeOrder[a.type] !== typeOrder[b.type]) {
                return typeOrder[a.type] - typeOrder[b.type];
            }
            return a.code.localeCompare(b.code);
        });
    }, [accounts]);

    const getLedgerForAccount = (accountId) => {
        const account = accounts.find(a => a.id === accountId);
        if (!account) return [];

        let entries = [];
        let runningBalance = 0;

        journals
            .filter(j => {
                if (dateFrom && j.date < dateFrom) return false;
                if (dateTo && j.date > dateTo) return false;
                return j.lines.some(l => l.accountId === accountId);
            })
            .sort((a, b) => new Date(a.date) - new Date(b.date))
            .forEach(journal => {
                journal.lines
                    .filter(l => l.accountId === accountId)
                    .forEach(line => {
                        const debit = line.type === 'debit' ? line.amount : 0;
                        const credit = line.type === 'credit' ? line.amount : 0;

                        if (inferAccountType(account) === ACCOUNT_TYPES.ASSET || inferAccountType(account) === ACCOUNT_TYPES.EXPENSE) {
                            runningBalance += debit - credit;
                        } else {
                            runningBalance += credit - debit;
                        }

                        const counterLines = journal.lines.filter(l => l.accountId !== accountId);
                        const counterAccounts = counterLines.map(l => accounts.find(a => a.id === l.accountId)).filter(Boolean);

                        entries.push({
                            journalId: journal.id,
                            date: journal.date,
                            description: journal.description,
                            counterAccounts,
                            debit,
                            credit,
                            balance: runningBalance
                        });
                    });
            });

        return entries;
    };

    const allLedgerData = useMemo(() => {
        if (selectedAccount !== 'all') return [];
        return sortedAccounts
            .map(account => {
                const entries = getLedgerForAccount(account.id);
                if (entries.length === 0) return null;
                return { account, entries };
            })
            .filter(Boolean);
    }, [selectedAccount, journals, accounts, sortedAccounts, dateFrom, dateTo]);

    const singleLedgerData = useMemo(() => {
        if (selectedAccount === 'all' || !selectedAccount) return [];
        return getLedgerForAccount(selectedAccount);
    }, [selectedAccount, journals, accounts, dateFrom, dateTo]);

    const selectedAccountInfo = selectedAccount !== 'all' ? accounts.find(a => a.id === selectedAccount) : null;

    return (
        <div>
            <div className="page-header">
                <h2>総勘定元帳</h2>
            </div>

            <div className="card">
                <div className="filter-row" style={{alignItems: 'flex-end', flexWrap: 'wrap', gap: '16px'}}>
                    <div className="form-group" style={{minWidth: '200px', flex: '0 0 auto'}}>
                        <label>勘定科目</label>
                        <div style={{display: 'flex', gap: '4px', alignItems: 'center', flexWrap: 'wrap'}}>
                            <button
                                className={`btn ${selectedAccount === 'all' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setSelectedAccount('all')}
                                style={{padding: '8px 12px', whiteSpace: 'nowrap'}}
                            >
                                全科目
                            </button>
                            <button
                                className={`btn ${selectedAccount === '_NULL_' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setSelectedAccount('_NULL_')}
                                style={{padding: '8px 12px', whiteSpace: 'nowrap', backgroundColor: selectedAccount === '_NULL_' ? '#f39c12' : '#fffbe6', borderColor: '#f39c12', color: selectedAccount === '_NULL_' ? '#fff' : '#8a6d3b'}}
                            >
                                記入漏れ
                            </button>
                            <button
                                className={`btn ${selectedAccount === '_MODIFIED_' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setSelectedAccount('_MODIFIED_')}
                                style={{padding: '8px 12px', whiteSpace: 'nowrap', backgroundColor: selectedAccount === '_MODIFIED_' ? '#3498db' : '#e8f4fc', borderColor: '#3498db', color: selectedAccount === '_MODIFIED_' ? '#fff' : '#2980b9'}}
                            >
                                変更履歴
                            </button>
                            <button
                                className={`btn ${selectedAccount === '_DELETED_' ? 'btn-primary' : 'btn-secondary'}`}
                                onClick={() => setSelectedAccount('_DELETED_')}
                                style={{padding: '8px 12px', whiteSpace: 'nowrap', backgroundColor: selectedAccount === '_DELETED_' ? '#7f8c8d' : '#f5f5f5', borderColor: '#7f8c8d', color: selectedAccount === '_DELETED_' ? '#fff' : '#666'}}
                            >
                                削除済
                            </button>
                            <div style={{flex: 1, minWidth: '180px'}}>
                                <AccountSelect
                                    accounts={sortedAccounts}
                                    value={['all', '_NULL_', '_DELETED_', '_MODIFIED_'].includes(selectedAccount) ? '' : selectedAccount}
                                    onChange={(id) => setSelectedAccount(id)}
                                    placeholder="科目を検索..."
                                />
                            </div>
                        </div>
                    </div>
                    <div className="form-group" style={{flex: '1 1 auto'}}>
                        <label>期間</label>
                        <MonthSelector
                            selectedMonths={selectedMonths}
                            setSelectedMonths={setSelectedMonths}
                            currentFiscalYear={currentFiscalYear}
                            isKojin={isKojin}
                        />
                    </div>
                </div>

                {selectedAccount === '_NULL_' ? (
                    <>
                        <h3 className="card-title" style={{marginTop: '20px', backgroundColor: '#fffbe6', padding: '10px', borderRadius: '4px'}}>
                            記入漏れ検索
                            <span style={{fontSize: '12px', fontWeight: 'normal', marginLeft: '10px', color: '#8a6d3b'}}>
                                日付・摘要・科目・金額のいずれかが空白の取引
                            </span>
                        </h3>
                        {nullEntries.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th>日付</th>
                                        <th>摘要</th>
                                        <th>借方科目</th>
                                        <th className="text-right">借方金額</th>
                                        <th>貸方科目</th>
                                        <th className="text-right">貸方金額</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {nullEntries.map((entry, idx) => {
                                        const debitLines = entry.lines.filter(l => l.type === 'debit');
                                        const creditLines = entry.lines.filter(l => l.type === 'credit');
                                        return (
                                            <tr
                                                key={idx}
                                                onClick={() => jumpToJournal(entry.journalId)}
                                                style={{cursor: 'pointer', backgroundColor: '#fffbe6'}}
                                            >
                                                <td>{entry.date ? formatDate(entry.date) : '(空白)'}</td>
                                                <td>{entry.description || '(空白)'}</td>
                                                <td>{debitLines.map(l => accounts.find(a => a.id === l.accountId)?.name || '(空白)').join(', ')}</td>
                                                <td className="text-right amount">{debitLines.map(l => l.amount ? `¥${formatCurrency(l.amount)}` : '(空白)').join(', ')}</td>
                                                <td>{creditLines.map(l => accounts.find(a => a.id === l.accountId)?.name || '(空白)').join(', ')}</td>
                                                <td className="text-right amount">{creditLines.map(l => l.amount ? `¥${formatCurrency(l.amount)}` : '(空白)').join(', ')}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-state" style={{backgroundColor: '#fffbe6'}}>
                                <h3>記入漏れのある取引はありません</h3>
                            </div>
                        )}
                    </>
                ) : selectedAccount === '_DELETED_' ? (
                    <>
                        <h3 className="card-title" style={{marginTop: '20px', backgroundColor: '#f5f5f5', padding: '10px', borderRadius: '4px'}}>
                            削除済み検索
                        </h3>
                        {deletedEntries.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th>削除日時</th>
                                        <th>元の日付</th>
                                        <th>摘要</th>
                                        <th className="text-right">金額</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {deletedEntries.map((entry, idx) => (
                                        <tr key={idx} style={{backgroundColor: '#f5f5f5'}}>
                                            <td>{entry.deletedAt && !isNaN(new Date(entry.deletedAt))
                                                ? new Date(entry.deletedAt).toLocaleString('ja-JP')
                                                : '(日時不明)'}</td>
                                            <td>{formatDate(entry.date)}</td>
                                            <td>{entry.description}</td>
                                            <td className="text-right amount">
                                                ¥{formatCurrency(entry.lines.filter(l => l.type === 'debit').reduce((s, l) => s + l.amount, 0))}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-state" style={{backgroundColor: '#f5f5f5'}}>
                                <h3>削除した取引はありません</h3>
                            </div>
                        )}
                    </>
                ) : selectedAccount === '_MODIFIED_' ? (
                    <>
                        <h3 className="card-title" style={{marginTop: '20px', backgroundColor: '#e8f4fc', padding: '10px', borderRadius: '4px'}}>
                            変更履歴
                        </h3>
                        {loadingModified ? (
                            <div className="loading-container">
                                <div className="spinner"></div>
                                <p>読み込み中...</p>
                            </div>
                        ) : modifiedEntries.length > 0 ? (
                            <div style={{display: 'flex', flexDirection: 'column', gap: '16px'}}>
                                {modifiedEntries.map((entry, idx) => {
                                    const renderJournalDiff = (before, after, opType) => {
                                        if (opType === 'CREATE') {
                                            return (
                                                <div style={{padding: '12px', backgroundColor: '#f0fdf4', borderRadius: '6px'}}>
                                                    <div style={{fontWeight: 'bold', marginBottom: '8px'}}>作成内容:</div>
                                                    <div><strong>日付:</strong> {after?.date}</div>
                                                    <div><strong>摘要:</strong> {after?.description}</div>
                                                    {after?.lines?.map((line, i) => (
                                                        <div key={i} style={{marginTop: '4px'}}>
                                                            <strong>{line.type === 'debit' ? '借方' : '貸方'}:</strong> {accounts.find(a => a.id === line.accountId)?.name} ¥{formatCurrency(line.amount)}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        }

                                        if (opType === 'DELETE') {
                                            return (
                                                <div style={{padding: '12px', backgroundColor: '#fdf2f2', borderRadius: '6px'}}>
                                                    <div style={{fontWeight: 'bold', marginBottom: '8px'}}>削除内容:</div>
                                                    <div><strong>日付:</strong> {before?.date}</div>
                                                    <div><strong>摘要:</strong> {before?.description}</div>
                                                    {before?.lines?.map((line, i) => (
                                                        <div key={i} style={{marginTop: '4px'}}>
                                                            <strong>{line.type === 'debit' ? '借方' : '貸方'}:</strong> {accounts.find(a => a.id === line.accountId)?.name} ¥{formatCurrency(line.amount)}
                                                        </div>
                                                    ))}
                                                </div>
                                            );
                                        }

                                        // UPDATE: Show before and after with diff highlighting
                                        const dateChanged = before?.date !== after?.date;
                                        const descChanged = before?.description !== after?.description;

                                        return (
                                            <div style={{display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px'}}>
                                                <div style={{padding: '12px', backgroundColor: '#fef3f2', borderRadius: '6px', border: '1px solid #fca5a5'}}>
                                                    <div style={{fontWeight: 'bold', marginBottom: '8px', color: '#991b1b'}}>変更前:</div>
                                                    <div><strong>日付:</strong> <span style={dateChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>{before?.date}</span></div>
                                                    <div><strong>摘要:</strong> <span style={descChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>{before?.description}</span></div>
                                                    {before?.lines?.map((line, i) => {
                                                        const afterLine = after?.lines?.find(l => l.type === line.type && after.lines.indexOf(l) === i);
                                                        const accountChanged = !afterLine || line.accountId !== afterLine.accountId;
                                                        const amountChanged = !afterLine || line.amount !== afterLine.amount;
                                                        return (
                                                            <div key={i} style={{marginTop: '4px'}}>
                                                                <strong>{line.type === 'debit' ? '借方' : '貸方'}:</strong>{' '}
                                                                <span style={accountChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>
                                                                    {accounts.find(a => a.id === line.accountId)?.name}
                                                                </span>{' '}
                                                                <span style={amountChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>
                                                                    ¥{formatCurrency(line.amount)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                                <div style={{padding: '12px', backgroundColor: '#eff6ff', borderRadius: '6px', border: '1px solid #93c5fd'}}>
                                                    <div style={{fontWeight: 'bold', marginBottom: '8px', color: '#1e40af'}}>変更後:</div>
                                                    <div><strong>日付:</strong> <span style={dateChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>{after?.date}</span></div>
                                                    <div><strong>摘要:</strong> <span style={descChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>{after?.description}</span></div>
                                                    {after?.lines?.map((line, i) => {
                                                        const beforeLine = before?.lines?.find(l => l.type === line.type && before.lines.indexOf(l) === i);
                                                        const accountChanged = !beforeLine || line.accountId !== beforeLine.accountId;
                                                        const amountChanged = !beforeLine || line.amount !== beforeLine.amount;
                                                        return (
                                                            <div key={i} style={{marginTop: '4px'}}>
                                                                <strong>{line.type === 'debit' ? '借方' : '貸方'}:</strong>{' '}
                                                                <span style={accountChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>
                                                                    {accounts.find(a => a.id === line.accountId)?.name}
                                                                </span>{' '}
                                                                <span style={amountChanged ? {color: '#dc2626', fontWeight: 'bold'} : {}}>
                                                                    ¥{formatCurrency(line.amount)}
                                                                </span>
                                                            </div>
                                                        );
                                                    })}
                                                </div>
                                            </div>
                                        );
                                    };

                                    return (
                                        <div
                                            key={idx}
                                            style={{
                                                border: '2px solid #ddd',
                                                borderRadius: '8px',
                                                padding: '16px',
                                                backgroundColor: '#fff'
                                            }}
                                        >
                                            <div style={{marginBottom: '12px', paddingBottom: '12px', borderBottom: '1px solid #e5e7eb'}}>
                                                <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                                                    <span style={{fontWeight: 'bold', fontSize: '15px', color: entry.operationType === 'DELETE' ? '#dc2626' : '#2563eb'}}>
                                                        {entry.operationType === 'DELETE' ? '【削除】' : '【修正】'}
                                                    </span>
                                                    <div style={{fontSize: '13px', color: '#666'}}>
                                                        {entry.operationTimestamp && !isNaN(new Date(entry.operationTimestamp))
                                                            ? new Date(entry.operationTimestamp).toLocaleString('ja-JP')
                                                            : '(日時不明)'}
                                                    </div>
                                                </div>
                                                <div style={{fontSize: '12px', color: '#888', marginTop: '4px'}}>
                                                    操作者: {entry.operatorName || entry.operatorEmail}
                                                </div>
                                            </div>
                                            {renderJournalDiff(entry.beforeSnapshot, entry.afterSnapshot, entry.operationType)}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="empty-state" style={{backgroundColor: '#e8f4fc'}}>
                                <h3>変更履歴はありません</h3>
                            </div>
                        )}
                    </>
                ) : selectedAccount === 'all' ? (
                    allLedgerData.length > 0 ? (
                        allLedgerData.map(({ account, entries }) => (
                            <div key={account.id} style={{marginBottom: '30px'}}>
                                <h3 className="card-title" style={{marginTop: '20px'}}>
                                    {account.code} {account.name}
                                    <span className={`badge ${ACCOUNT_TYPE_BADGES[account.type]}`} style={{marginLeft: '10px'}}>
                                        {ACCOUNT_TYPE_LABELS[account.type]}
                                    </span>
                                </h3>
                                <table>
                                    <thead>
                                        <tr>
                                            <th style={{width: '60px'}}>日付</th>
                                            <th>摘要</th>
                                            <th style={{width: '120px'}}>科目</th>
                                            <th className="text-right" style={{width: '100px'}}>借方</th>
                                            <th className="text-right" style={{width: '100px'}}>貸方</th>
                                            <th className="text-right" style={{width: '100px'}}>残高</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {entries.map((entry, idx) => (
                                            <tr key={idx} onClick={() => jumpToJournal(entry.journalId)} style={{cursor: 'pointer'}}>
                                                <td style={{fontSize: '12px'}}>{entry.date.slice(5).replace('-', '/')}</td>
                                                <td>{entry.description}</td>
                                                <td>{entry.counterAccounts?.length > 1 ? '諸口' : entry.counterAccounts?.[0]?.name || ''}</td>
                                                <td className="text-right amount">{entry.debit > 0 ? `¥${formatCurrency(entry.debit)}` : ''}</td>
                                                <td className="text-right amount">{entry.credit > 0 ? `¥${formatCurrency(entry.credit)}` : ''}</td>
                                                <td className="text-right amount">¥{formatCurrency(entry.balance)}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ))
                    ) : (
                        <div className="empty-state">
                            <h3>取引がありません</h3>
                        </div>
                    )
                ) : selectedAccount ? (
                    <>
                        <h3 className="card-title">
                            {selectedAccountInfo?.code} {selectedAccountInfo?.name}
                            <span className={`badge ${ACCOUNT_TYPE_BADGES[selectedAccountInfo?.type]}`} style={{marginLeft: '10px'}}>
                                {ACCOUNT_TYPE_LABELS[selectedAccountInfo?.type]}
                            </span>
                        </h3>
                        {singleLedgerData.length > 0 ? (
                            <table>
                                <thead>
                                    <tr>
                                        <th style={{width: '60px'}}>日付</th>
                                        <th>摘要</th>
                                        <th style={{width: '120px'}}>科目</th>
                                        <th className="text-right" style={{width: '100px'}}>借方</th>
                                        <th className="text-right" style={{width: '100px'}}>貸方</th>
                                        <th className="text-right" style={{width: '100px'}}>残高</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {singleLedgerData.map((entry, idx) => (
                                        <tr key={idx} onClick={() => jumpToJournal(entry.journalId)} style={{cursor: 'pointer'}}>
                                            <td style={{fontSize: '12px'}}>{entry.date.slice(5).replace('-', '/')}</td>
                                            <td>{entry.description}</td>
                                            <td>{entry.counterAccounts?.length > 1 ? '諸口' : entry.counterAccounts?.[0]?.name || ''}</td>
                                            <td className="text-right amount">{entry.debit > 0 ? `¥${formatCurrency(entry.debit)}` : ''}</td>
                                            <td className="text-right amount">{entry.credit > 0 ? `¥${formatCurrency(entry.credit)}` : ''}</td>
                                            <td className="text-right amount">¥{formatCurrency(entry.balance)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="empty-state">
                                <h3>取引はありません</h3>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        </div>
    );
};

export default GeneralLedger;
